<template>
    <BaseForm v-if="seriesId && defaultLocations" @submit.prevent>
        <template #fields>
            <TextField
                :value="values.nameShort"
                :label="$t('name-short')"
                :error-messages="errors.nameShort"
                :required="true"
                @input="setFieldValue('nameShort', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('nameShort', true)"
            />
            <TextField
                :value="values.nameLong"
                :label="$t('name-long')"
                :error-messages="errors.nameLong"
                :required="true"
                @input="setFieldValue('nameLong', ($event.target as HTMLInputElement).value)"
                @change="setFieldValue('nameLong', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('nameLong', true)"
            />
            <NumberField
                :value="values.year"
                :label="$t('execution-year')"
                :error-messages="errors.year"
                :required="true"
                @input="setFieldValue('year', ($event.target as HTMLInputElement).value)"
                @change="setFieldValue('year', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('year', true)"
            />
        </template>
        <template #buttons>
            <Button v-if="isTouched" size="Base" variant="Danger-Light" @click="onDiscard">
                <template #icon>
                    <UndoIcon class="size-5" />
                </template>
                {{ $t('discard') }}
            </Button>
            <Button v-else size="Base" variant="Neutral-Light" @click="$router.back">
                <template #icon>
                    <CloseIcon class="size-5" />
                </template>
                {{ $t('abort') }}
            </Button>
            <Button
                size="Base"
                variant="Info-Strong"
                :disabled-explanation="saveButtonDisabledExplanation"
                type="submit"
                @click="onSave"
            >
                <template #icon>
                    <AllDoneIcon class="size-5" />
                </template>
                {{ $t('create') }}
            </Button>
        </template>
    </BaseForm>
</template>
<script setup lang="ts">
import Button from '@/components/Button/Button.vue';
import BaseForm from '@/components/Form/BaseForm.vue';
import NumberField from '@/components/Form/NumberField.vue';
import TextField from '@/components/Form/TextField.vue';
import AllDoneIcon from '@/components/Icon/AllDoneIcon.vue';
import CloseIcon from '@/components/Icon/CloseIcon.vue';
import UndoIcon from '@/components/Icon/UndoIcon.vue';
import { useForm } from '@/forms/composables/useForm';
import { GetExternalEventDefaultLocationsDocument } from '@/generated/graphql';
import { $t } from '@/plugins/fluent';
import { useQuery } from '@vue/apollo-composable';
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useExternalEventEventCreate } from '../composables/useExternalEventEventCreate';
import { useExternalEventVisibilities } from '../composables/useExternalEventVisibilities';
import { ExternalEventEventCreateSchema } from '../schemas';

const router = useRouter();
const props = defineProps<{ seriesId: string }>();
const seriesId = computed(() => props.seriesId);

const { result } = useQuery(GetExternalEventDefaultLocationsDocument);
const defaultLocations = computed(() => result.value?.externalEventLocations?.nodes ?? []);

const {
    values,
    errors,
    saveButtonDisabledExplanation,
    isTouched,
    setFieldTouched,
    setFieldValue,
    validate,
    onDiscard,
} = useForm(
    ExternalEventEventCreateSchema,
    computed(() => ({
        nameShort: '',
        nameLong: '',
        year: '',
    })),
);

const { create, createInternationalLocation } = useExternalEventEventCreate(values, seriesId);
const { createDefaultVisibilities } = useExternalEventVisibilities();

async function onSave() {
    const valid = await validate();
    if (valid) {
        const { event } = await create();
        if (event) {
            const national = event.series.national;
            let locationIds: string[] = [];
            if (national) {
                locationIds = defaultLocations.value.map((l) => l.id);
            } else {
                // create international location
                const createLocationResult = await createInternationalLocation(event.id, event.nameShort);
                const locationId = createLocationResult?.data?.updateExternalEvent?.externalEvent?.locationId;
                if (!locationId) {
                    throw new Error('Could not create initial international location');
                }
                locationIds = [locationId];
            }
            await createDefaultVisibilities(event.id, locationIds);
            router.push({ name: 'external-event-event', params: { eventId: event.id } });
        }
    }
}
</script>
